import {
  AudioTrackLane,
  ImageButton,
  MarkerLane,
  MomentMarker,
  OmakasePlayer,
  PeriodMarker,
  SubtitlesLane,
  SubtitlesVttTrack,
  TextLabel,
  ThumbnailLane,
} from '@byomakase/omakase-player';
import {
  ContentPlaybackTimelineAudioTrack,
  ContentPlaybackWaveformReference,
  CustomMarker,
  CustomMarkerStyle,
  FileWithProxy,
} from './video-player.types';
import { alpha } from '@mui/material';

const getExpandOrCollapseIcon = (expand: boolean, isLightMode: boolean) => ({
  src: !expand ? (!isLightMode ? '/images/chevron-down.svg' : '/images/chevron-down-dark.svg') : (!isLightMode ? '/images/chevron-right.svg' : '/images/chevron-right-dark.svg'),
  width: 16,
  height: 16,
  listening: true,
});

export const addBlackSegmentsLane = (omakasePlayer: OmakasePlayer, vttUrl: string, style: CustomMarkerStyle) => {
  const blacksMarkersLane = new MarkerLane({
    id: 'black_segments_lane',
    description: '',
    vttUrl,
    style: {
      backgroundFill: style.backgroundFill,
      height: 30,
      leftBackgroundFill: style.leftBackgroundFill,
      marginBottom: 1,
    },
    markerCreateFn: (cue) => {
      return new PeriodMarker({
        timeObservation: {
          start: cue.startTime,
          end: cue.endTime,
        },
        text: `${cue.text}`,
        editable: false,
        style: {
          renderType: 'lane',
          color: style.markerSquareBackgroundFill,
          symbolType: 'none',
          selectedAreaOpacity: 0,
          lineOpacity: 0,
        },
      });
    },
    markerProcessFn: (marker) => {
      marker.onClick$.subscribe({
        next: () => {
          omakasePlayer.video.seekToTime((marker.timeObservation as any)?.start);
        },
      });
    },
  });

  if (omakasePlayer.timeline) {

    blacksMarkersLane.addTimelineNode({
      width: 150,
      height: 20,
      justify: 'start',
      margin: [0, 0, 0, 0],
      timelineNode: createLabel('Black segments', style.labelTextColor),
    });
    omakasePlayer.timeline.addTimelineLane(blacksMarkersLane);
  }

  return blacksMarkersLane;
};

export const addPointsOfInterests = (omakasePlayer: OmakasePlayer, vttUrl: string, style: CustomMarkerStyle) => {
  const poiLane = new MarkerLane({
    description: '',
    vttUrl,
    style: {
      backgroundFill: style.backgroundFill,
      height: 30,
      leftBackgroundFill: style.leftBackgroundFill,
      marginBottom: 1,
    },
    markerCreateFn: (cue) => {
      return new MomentMarker({
        timeObservation: {
          time: cue.startTime,
        },
        text: `${cue.text}`,
        editable: false,
        style: {
          renderType: 'lane',
          color: style.poiBackgroundFill,
          symbolType: 'circle',
        },
      });
    },
    markerProcessFn: (marker) => {
      marker.onClick$.subscribe({
        next: () => {
          omakasePlayer.video.seekToTime((marker.timeObservation as any)?.time);
        },
      });
    },
  });
  if (omakasePlayer.timeline) {
    poiLane.addTimelineNode({
      width: 150,
      height: 20,
      justify: 'start',
      margin: [0, 0, 0, 0],
      timelineNode: createLabel('Points of Interest (POI)', style.labelTextColor),
    });
    omakasePlayer.timeline.addTimelineLane(poiLane);
  }
  return poiLane;
};

export const addThumbnails = (omakasePlayer: OmakasePlayer, vttUrl: string, style: CustomMarkerStyle) => {
  if (!omakasePlayer.timeline) {
    return;
  }
  const defaultThumbnailLane = new ThumbnailLane({
    id: 'thumbnail_lane_default',
    description: '',
    vttUrl,
    style: {
      descriptionTextFill: style.labelTextColor,
      backgroundFill: style.backgroundFill,
      height: 50,
      leftBackgroundFill: style.leftBackgroundFill,
      thumbnailHoverScale: 2,
      marginBottom: 1,
    },
  });

  //automatic position to thumb start frame
  defaultThumbnailLane.onClick$.subscribe((event) => {
    omakasePlayer.video.seekToTime(event?.thumbnail?.cue?.startTime ?? 0);
  });


  defaultThumbnailLane.addTimelineNode({
    width: 150,
    height: 20,
    justify: 'start',
    margin: [0, 0, 0, 0],
    timelineNode: createLabel('Thumbnails', style.labelTextColor),
  });
  omakasePlayer.timeline.addTimelineLane(defaultThumbnailLane);
  return defaultThumbnailLane;
};

export const addExternalSubtitle = (omakasePlayer: OmakasePlayer, asset: FileWithProxy, isDefault: boolean, style: CustomMarkerStyle) => {
  return addEnclosedCaptionLane(omakasePlayer, asset, isDefault, style);
};

export const createVttTrack = (omakasePlayer: OmakasePlayer, subtitle: FileWithProxy): Promise<SubtitlesVttTrack> => {
  return new Promise((resolve, reject) => {
    omakasePlayer.subtitles
    .createVttTrack({
      id: subtitle.id?.toString(),
      src: subtitle.proxy.url,
      label: subtitle.name,
      language: '',
      default: false,
    }).subscribe({
      next: (track) => {
        resolve(track);
      },
      error: (err) => reject(err),
    });
  });
}

export const addEnclosedCaptionLane = async (omakasePlayer: OmakasePlayer, subtitle: FileWithProxy, isDefault: boolean, style: CustomMarkerStyle) => {
  if (!omakasePlayer.timeline) {
    return;
  }

  if (omakasePlayer.timeline.getTimelineLane(subtitle.id?.toString())) {
    omakasePlayer.timeline.removeTimelineLane(subtitle.id?.toString());
  }

  const track = isDefault ? subtitle : await createVttTrack(omakasePlayer, subtitle);
  const subtitleLane = new SubtitlesLane({
    id: subtitle.id.toString(),
    description: '',
    vttUrl: isDefault ? subtitle.url : subtitle.proxy.url,
    style: {
      backgroundFill: style.backgroundFill,
      height: 40,
      leftBackgroundFill: style.leftBackgroundFill,
      subtitlesLaneItemOpacity: 0.7,
      fontFamily: 'DM Sans',
      subtitlesLaneItemFill: style.markerSquareBackgroundFill,
      paddingTop: 10,
      paddingBottom: 10,
      marginBottom: 1,
    },
  });

  let ind = omakasePlayer.timeline.getTimelineLanes().findIndex((item) => item instanceof SubtitlesLane);
  if (ind !== -1) {
    omakasePlayer.timeline.addTimelineLaneAtIndex(subtitleLane, ind + 1);
  } else {
    // always add before audio tracks by default
    ind = omakasePlayer.timeline.getTimelineLanes().findIndex((item) => item instanceof AudioTrackLane);
    if (ind !== -1) {
      omakasePlayer.timeline.addTimelineLaneAtIndex(subtitleLane, ind);
    } else {
      omakasePlayer.timeline.addTimelineLane(subtitleLane);
    }
  }

  const textLabel = new TextLabel({
    text: 'OFF',
    listening: true,
    style: {
      align: 'center',
      verticalAlign: 'middle',
      fill: style.darkTextColor,
      fontFamily: 'DM Sans',
      backgroundFill: style.markerSquareBackgroundFill,
      backgroundBorderRadius: 3,
    },
  });

  omakasePlayer.subtitles.onShow$.subscribe(() => {
    if (subtitle.id.toString() !== omakasePlayer?.subtitles?.getActiveTrack()?.id) {
      textLabel.text = 'OFF';
      textLabel.style = {
        backgroundFill: style.markerSquareBackgroundFill,
      };
    } else {
      textLabel.style = {
        backgroundFill: style.success,
      };
    }
  });

  textLabel.onClick$.subscribe({
    next: () => {
      const tracks = omakasePlayer.subtitles.getTracks();
      const activeTrack = omakasePlayer.subtitles.getActiveTrack();
      const trackLookup = isDefault ? tracks?.find((t) => t.embedded) : tracks.find((t) => t.id === track.id);
      if (trackLookup && subtitle?.id.toString() !== activeTrack?.id) {
        omakasePlayer.subtitles.showTrack(trackLookup.id);
        textLabel.text = 'ON';
      } else {
        if (activeTrack?.hidden && subtitle.id.toString() === activeTrack?.id) {
          omakasePlayer.subtitles.showActiveTrack();
          textLabel.text = 'ON';
        } else {
          omakasePlayer.subtitles.hideActiveTrack();
          textLabel.text = 'OFF';
          textLabel.style = {
            ...textLabel.style,
            backgroundFill: style.markerSquareBackgroundFill,
          };
        }
      }
    },
  });

  subtitleLane.addTimelineNode({
    width: 30,
    height: 22,
    justify: 'end',
    margin: [0, 5, 0, 0],
    timelineNode: textLabel,
  });


  subtitleLane.addTimelineNode({
    width: 150,
    height: 20,
    justify: 'start',
    margin: [0, 0, 0, 0],
    timelineNode: createLabel(subtitle.name, style.labelTextColor),
  });
  return subtitleLane;
}

export const createInOutMarkerLane = (
  omakasePlayer: OmakasePlayer,
  markers: CustomMarker[],
  style: CustomMarkerStyle,
  isLightMode: boolean,
  onMarkerFocus?: (markerId: string | undefined) => void,
  focusedMarkerId?: string,
) => {
  if (!omakasePlayer.timeline) {
    return;
  }

  if (omakasePlayer.timeline.getTimelineLane('in_and_out_markers_lane')) {
    omakasePlayer.timeline.removeTimelineLane('in_and_out_markers_lane');
  }
  const inAndOutMarkersLane = new MarkerLane({
    id: 'in_and_out_markers_lane',
    description: '',
    style: {
      backgroundFill: style.backgroundFill,
      height: 30,
      leftBackgroundFill: style.leftBackgroundFill,
      marginBottom: 0,
    },
  });
  const expandCollapseButton = new ImageButton(getExpandOrCollapseIcon(true, isLightMode));

  const linkedMarkersByCustomId = new Map<string, { topMarkerId: string; subLaneId: string; subMarkerId: string }>();

  const syncSelectedMarker = (lane: MarkerLane, markerId: string | undefined) => {
    if (!markerId) return;
    if (lane.getSelectedMarker()?.id === markerId) return;
    lane.toggleMarker(markerId);
  };

  const handleMarkerFocus = (event: any) => {
    const focusedCustomMarkerId = event?.marker?.data?.customMarkerId as string | undefined;
    if (focusedCustomMarkerId) {
      onMarkerFocus?.(focusedCustomMarkerId);
    }

    const linkedMarkers = focusedCustomMarkerId ? linkedMarkersByCustomId.get(focusedCustomMarkerId) : undefined;
    if (!linkedMarkers || !omakasePlayer.timeline) {
      return;
    }

    syncSelectedMarker(inAndOutMarkersLane, linkedMarkers.topMarkerId);
  };

  inAndOutMarkersLane.onMarkerFocus$.subscribe({
    next: handleMarkerFocus,
  });

  omakasePlayer.timeline.addTimelineLaneAtIndex(inAndOutMarkersLane, 1);
  inAndOutMarkersLane.addTimelineNode({
    width: 20,
    height: 20,
    justify: 'start',
    margin: [3, 0, 0, 0],
    timelineNode: expandCollapseButton,
  });

  inAndOutMarkersLane.addTimelineNode({
    width: 150,
    height: 20,
    justify: 'start',
    margin: [0, 0, 0, 0],
    timelineNode: createLabel('Custom Markers', style.labelTextColor),
  });
  const countLabel = new TextLabel({
    text: markers.length.toString(),
    listening: true,
    style: {
      align: 'center',
      verticalAlign: 'middle',
      fill: style.darkTextColor,
      fontFamily: 'DM Sans',
      backgroundFill: style.lightTextColor,
      backgroundBorderRadius: 2,
      fontSize: 10,
    },
  });
  inAndOutMarkersLane.addTimelineNode({
    width: 20,
    height: 20,
    justify: 'end',
    margin: [0, 5, 0, 0],
    timelineNode: countLabel,
  });

  const markerLanes = markers.map((marker, index) => {
    const isFocused = marker.id === focusedMarkerId;
    const id = `sub_lane_${marker.id}`;

    if (omakasePlayer?.timeline?.getTimelineLane(id)) {
      omakasePlayer.timeline.removeTimelineLane(id);
    }

    const inOutMarker = inAndOutMarkersLane.createPeriodMarker({
      text: marker.label + index,
      timeObservation: {
        start: marker.start,
        end: marker.end,
      },
      style: {
        symbolType: 'triangle',
        color: marker.color || '#fff',
        renderType: 'lane',
        selectedAreaOpacity: isFocused ? 0.2 : 0,
        lineOpacity: isFocused ? 0.8 : 0,
        markerHandleAreaOpacity: 1,
        lineStrokeWidth: isFocused ? 1 : 0,
      },
      editable: true,
    });
    inOutMarker.data = {
      ...(inOutMarker.data || {}),
      customMarkerId: marker.id,
    };

    const markerLane = new MarkerLane({
      id,
      description: '',
      style: {
        backgroundFill: style.backgroundFill,
        height: 30,
        leftBackgroundFill: style.leftBackgroundFill,
        marginBottom: 1,
        descriptionTextFill: style.labelTextColor,
        descriptionTextFontSize: 10,
        descriptionTextFontStyle: 'DM Sans',
      },
    });

    if (omakasePlayer.timeline) {
      omakasePlayer.timeline.addTimelineLaneAtIndex(markerLane, 2 + index);
    }

    markerLane.addTimelineNode({
      width: 150,
      height: 20,
      justify: 'start',
      margin: [0, 0, 0, 20],
      timelineNode: createLabel(marker.label, style.labelTextColor),
    });
    markerLane.onMarkerFocus$.subscribe({
      next: handleMarkerFocus,
    });

    const subMarker = markerLane.createPeriodMarker({
      text: '',
      timeObservation: {
        start: marker.start,
        end: marker.end,
      },
      style: {
        symbolType: 'triangle',
        color: marker.color || '#ffffff',
        renderType: 'lane',
        selectedAreaOpacity: isFocused ? 0.2 : 0,
        lineOpacity: isFocused ? 0.8 : 0,
        markerHandleAreaOpacity: 1,
        lineStrokeWidth: isFocused ? 1 : 0,
      },
      editable: true,
    });
    subMarker.data = {
      ...(subMarker.data || {}),
      customMarkerId: marker.id,
    };

    linkedMarkersByCustomId.set(marker.id, {
      topMarkerId: inOutMarker.id,
      subLaneId: id,
      subMarkerId: subMarker.id,
    });

    markerLane.minimize();

    return markerLane;
  });

  expandCollapseButton.onClick$.subscribe({
    next: () => {
      const isMinimized = markerLanes.some((lane) => lane.isMinimized());
      const imageConfig = getExpandOrCollapseIcon(!isMinimized, isLightMode);
      expandCollapseButton.setImage(imageConfig);
      markerLanes.forEach((lane) => {
        if (isMinimized) {
          lane.maximize();
        } else {
          lane.minimize();
        }
      });
    }
  });
  return inAndOutMarkersLane;
}

const createLabel = (text: string, labelTextColor: string | undefined, style: any = {}) => {
  return new TextLabel({
    text,
    listening: false,
    style: {
      align: 'left',
      verticalAlign: 'middle',
      fill: labelTextColor,
      fontSize: 12,
      fontFamily: 'DM Sans',
      ...style,
    },
  });
}

const createNewAudioTrackLane = (omakasePlayer: OmakasePlayer, enabled: boolean, id: string, description: string, channelLabel: string, url: string, style: CustomMarkerStyle, isLightMode: boolean) => {
  const audioTrackLane = new AudioTrackLane({
    id: id,
    description: '',
    vttUrl: url,
    style: {
      backgroundFill: style.backgroundFill,
      paddingTop: 0,
      paddingBottom: 0,
      marginBottom: 1,
      height: 40,
      itemWidth: 3,
      itemMinPadding: 1,
      itemCornerRadius: 2,
      maxSampleFillLinearGradientColorStops: [1, '#9FE6DD'],
      minSampleFillLinearGradientColorStops: [1, '#9FE6DD'],
      leftBackgroundFill: style.leftBackgroundFill,
      descriptionTextFill: style.labelTextColor,
    },
  });

  if (audioTrackLane && omakasePlayer.timeline) {
    omakasePlayer.timeline.addTimelineLane(audioTrackLane);
  }

  const expandCollapseButton = new ImageButton(getExpandOrCollapseIcon(true, isLightMode));
  audioTrackLane.addTimelineNode({
    width: 20,
    height: 20,
    justify: 'start',
    margin: [3, 0, 0, 0],
    timelineNode: expandCollapseButton,
  });

  audioTrackLane.addTimelineNode({
    width: 150,
    height: 20,
    justify: 'start',
    margin: [0, 0, 0, 0],
    timelineNode: createLabel(description, style.labelTextColor),
  });

  const textLabel = new TextLabel({
    text: channelLabel,
    listening: true,
    style: {
      align: 'center',
      verticalAlign: 'middle',
      fill: enabled ? style.darkTextColor : alpha(style.labelTextColor as string, 0.6),
      backgroundFill: enabled ? style.error : style.backgroundFill,
      backgroundBorderRadius: 3,
      fontSize: 12,
      fontFamily: 'DM Sans',
      fontStyle: 'bold',
    },
  });

  textLabel.onClick$.subscribe({
    next: () => {
      omakasePlayer.audio.setActiveAudioTrack(id).subscribe({
        next: () => console.info(`Audio track with id ${id} is now active`),
        error: (err) => console.error(`Failed to set active audio track with id ${id}:`, err),
      });
    },
  });

  omakasePlayer.audio.onAudioSwitched$.subscribe(() => {
    if (id !== omakasePlayer.audio.getActiveAudioTrack()?.id) {
      textLabel.style = {
        fill: alpha(style.labelTextColor as string, 0.6),
        backgroundFill: style.backgroundFill,
      };
    } else {
      textLabel.style = {
        fill: style.darkTextColor,
        backgroundFill: style.error,
      };
    }
  });

  audioTrackLane.addTimelineNode({
    width: 25,
    height: 25,
    justify: 'end',
    margin: [0, 0, 0, 5],
    timelineNode: textLabel,
  });
  audioTrackLane.addTimelineNode({
    width: 25,
    height: 25,
    justify: 'end',
    margin: [0, 0, 0, 0],
    timelineNode: new ImageButton({
      src: '/images/bi_volume-down-fill.svg',
      width: 25,
      height: 25,
      listening: false,
    }),
  });

  return { expandCollapseButton, lane: audioTrackLane };
}

const createNewSubAudioTrackLane = (omakasePlayer: OmakasePlayer, id: string, laneName: string, url: string, style: CustomMarkerStyle) => {
  const audioTrackLane = new AudioTrackLane({
    id: id,
    description: '',
    vttUrl: url,
    style: {
      backgroundFill: style.backgroundFill,
      paddingTop: 0,
      paddingBottom: 0,
      height: 40,
      itemWidth: 3,
      itemMinPadding: 1,
      itemCornerRadius: 2,
      maxSampleFillLinearGradientColorStops: [1, '#9FE6DD'],
      minSampleFillLinearGradientColorStops: [1, '#9FE6DD'],
      leftBackgroundFill: style.leftBackgroundFill,
      descriptionTextFill: style.labelTextColor,
    },
  });

  if (audioTrackLane && omakasePlayer.timeline) {
    omakasePlayer.timeline.addTimelineLane(audioTrackLane);
  }

  const textLabel = new TextLabel({
    text: `S`,
    listening: true,
    style: {
      align: 'center',
      verticalAlign: 'middle',
      fill: style.darkTextColor,
      backgroundFill: style.warning,
      backgroundBorderRadius: 3,
      fontSize: 10,
      fontFamily: 'DM Sans',
      fontStyle: 'bold',
    },
  });
  const textLabelM = new TextLabel({
    text: `M`,
    listening: true,
    style: {
      align: 'center',
      verticalAlign: 'middle',
      fill: style.lightTextColor,
      fontSize: 10,
      fontFamily: 'DM Sans',
      fontStyle: 'bold',
      offsetX: 0,
    },
  });

  audioTrackLane.addTimelineNode({
    width: 150,
    height: 20,
    justify: 'start',
    margin: [0, 0, 0, 0],
    timelineNode: createLabel(laneName, style.labelTextColor, {
      offsetX: -20,
    }),
  });
  audioTrackLane.addTimelineNode({
    width: 20,
    height: 20,
    justify: 'end',
    timelineNode: textLabel,
  });
  audioTrackLane.addTimelineNode({
    width: 20,
    height: 20,
    justify: 'end',
    timelineNode: textLabelM,
  });

  audioTrackLane.minimize();

  return audioTrackLane;
}

export const createAudioTracks = (omakasePlayer: OmakasePlayer, audioTracks: ContentPlaybackTimelineAudioTrack[], style: CustomMarkerStyle, isLightMode: boolean) => {
  const allLanes = audioTracks.flatMap((track, index) => {
    const name = `Audio Track ${index + 1}`;
    const defaultEnabled = index === 0;
    const chanelLabel = track.visual_reference.length.toFixed(1);
    const { expandCollapseButton, lane } = createNewAudioTrackLane(omakasePlayer, defaultEnabled, index.toString(), name, chanelLabel, track.visual_reference[0]?.url, style, isLightMode);
    const lanes = track.visual_reference.map((ref) => {
      const visualRefLane = ref as ContentPlaybackWaveformReference;
      const laneId = track.id + '_' + visualRefLane.channel;
      return createNewSubAudioTrackLane(omakasePlayer, laneId, visualRefLane.channel, visualRefLane.url, style);
    });

    expandCollapseButton.onClick$.subscribe({
      next: () => {
        const isMinimized = lanes.some((lane) => lane.isMinimized());
        const imageConfig = getExpandOrCollapseIcon(!isMinimized, isLightMode);
        expandCollapseButton.setImage(imageConfig);
        lanes.forEach((lane) => {
          if (isMinimized) {
            lane.maximize();
          } else {
            lane.minimize();
          }
        });
      }
    });

    return [lane, ...lanes];
  });

  return allLanes;
}

export const addAudioSidecar = (omakasePlayer: OmakasePlayer, customAudioUrl: string) => {
  omakasePlayer.audio.removeAllSidecarAudioTracks().subscribe({
    next: () => {
      omakasePlayer.audio.createSidecarAudioTrack({
        src: customAudioUrl,
        label: 'Custom dub',
        active: true,
      }).subscribe({
        next: (track) => omakasePlayer.audio.activateSidecarAudioTracks([track.id], true).subscribe(),
        error: (e) => console.error('Custom audio load failed', e),
      });
    }
  });
}

export const removeAllAudioSidecar = (omakasePlayer: OmakasePlayer) => {
  omakasePlayer.audio.removeAllSidecarAudioTracks();
}